/* eslint-disable react-hooks/exhaustive-deps */
import { Modal } from "react-bootstrap";
import { useEffect, useState, useMemo, Fragment, useCallback } from "react";
import queryString from "query-string";
import { useMutation, useQuery, useQueryClient } from "react-query";
import ReactPaginate from "react-paginate";
import { toast } from "react-toastify";
import "./../assets/scss/reports/cashbook.scss";
import useDebounce, { useQueryParams, useScrollTop } from "../utils/hooks";
import {
  paginationOptions,
  scrollToTop,
  sortOptions,
  VendorPayment,
  fetchActionsUtil,
} from "../utils/helpers";
import { useAuth } from "../hooks/useAuth";
import ConfirmDialog from "./ConfirmDialogue";
import PreviewFormOnlyComponent from "./Requisition/PreviewFormOnlyComponent";
import NoTableItem from "./utils/NoTableItem";
import PORequisitionModal from "./Requisition/PORequisition";
import { cloneDeep } from "lodash";
import { SelectedItemsTable } from "./RFQFunctions/SelectedItemsTable";
import { VendorListTable } from "./RFQFunctions/VendorListTable";
import { ItemTable } from "./RFQFunctions/ItemTable";
import { HeaderArea } from "./RFQFunctions/HeaderArea";

export default function RFQCompanyModal({
  show,
  onHide,
  selectedRFQ,
  disableInput,
  headerName,
  headerNo,
  theRFQName,
  requisition,
}) {
  const { backendUrl, user } = useAuth();
  const queryClient = useQueryClient();

  const initialFilterParams = { q: "", page: 1, limit: 40 };
  const normalizeQueryParams = (params) => ({
    ...params,
    onlyItems:
      params.onlyItems === true || params.onlyItems === "true" ? true : false,
  });
  const [queryParams, setQueryParams] = useQueryParams(initialFilterParams);
  const [filterParams, setFilterParams] = useState({
    ...initialFilterParams,
    ...normalizeQueryParams(queryParams),
  });
  const debouncedFilterParams = useDebounce(filterParams, 500);

  const [selectedFilledForm, setSelectedFilledForm] = useState(null);
  const [, setFetchingIframe] = useState(false);
  const [selectedID, setSelectedID] = useState("");
  const [tableData, setTableData] = useState([]);
  const [activeTableIndex, setActiveTableIndex] = useState(null);
  const [selectedData, setSelectedData] = useState([]);
  const [allOpenPos, setAllOpenPOs] = useState([]);
  const [ShowNewRequisitionModal, setShowNewRequisitionModal] = useState(false);
  const [vendorAttachments, setVendorAttachments] = useState([]);

  useScrollTop();

  useEffect(() => {
    setQueryParams((current) => ({ ...current, ...debouncedFilterParams }));
  }, [debouncedFilterParams, setQueryParams]);

  useEffect(() => {
    setFilterParams((current) => ({
      ...current,
      ...normalizeQueryParams(queryParams),
    }));
  }, [queryParams]);

  const fetchData = async ({ queryKey }) => {
    const [, RFQId, params] = queryKey;
    const data = await fetchActionsUtil(
      `${backendUrl}/api/vendors/particular-RFQ/${RFQId}?${queryString.stringify(
        params,
      )}`,
    );

    const vendorItems = data?.vendorRFQ?.flatMap((v) => v?.Quotes || []) || [];

    // console.log({ data, vendorItems });

    return {
      ...data,
      itemsData: [
        { label: "All Item", value: "" },
        ...(data.products || []).map((item) => ({
          label: item?.itemName,
          value: item?.itemName,
        })),
      ],
      vendorCert: [
        { label: "Select Vendor Cert", value: "" },
        ...(data?.vendorCert || []).map((cert) => ({
          label: cert,
          value: cert,
        })),
      ],
      vendorItems,
    };
  };

  const { data, refetch, isFetching } = useQuery(
    ["GET_PARTICULAR_RFQS", selectedRFQ?.RFQId, queryParams],
    fetchData,
    {
      enabled: Boolean(selectedRFQ?.RFQId),
      keepPreviousData: true,
    },
  );

  const vendorRFQ = data?.vendorRFQ || [];

  const handleSearchQueryChange = (e) => {
    const { name, value } = e.target;
    setFilterParams((prev) => ({
      ...prev,
      page: 1,
      [name]: value,
    }));
  };

  const showRFQ = async (formId, childId) => {
    const { form } = await fetchActionsUtil(
      `${backendUrl}/api/forms/single-filled-form?FormID=${formId}&childFormId=${childId}`,
      "GET",
    );
    setSelectedFilledForm(form);
  };

  const showRFQTable = async (vendor) => {
    const vendorData = vendorRFQ?.find((v) => v.vendor === vendor);

    setTableData(vendorData?.Quotes || []);
    setVendorAttachments(
      vendorData?.quoteAttachments
        ? JSON.parse(vendorData?.quoteAttachments)
        : [],
    );
  };

  const updateTableItem = ({ index, name, value }) => {
    setTableData((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [name]: value };
      return next;
    });
  };

  const AddPriceMutation = useMutation(
    (payload) =>
      fetchActionsUtil(
        `${backendUrl}/api/vendors/add-quote-price`,
        "post",
        "",
        payload,
      ),
    {
      onSuccess: ({ message }) => {
        toast.success(message);
        queryClient.invalidateQueries("GET_PARTICULAR_RFQS");
      },
      onError: ({ message = "" }) => {
        toast.error(`Unable to perform action: ${message}`);
      },
    },
  );

  const sendDataUpdate = async () => {
    const confirmed = await ConfirmDialog({
      title: "Save Price",
      description: "Are you sure you want to save the price",
    });

    if (confirmed) {
      AddPriceMutation.mutate({ tableData });
    }
  };

  const mergeUniqueItems = (current, items) => {
    const ids = new Set(current.map((item) => item.id));
    return [
      ...current,
      ...items.filter((item) => item.id != null && !ids.has(item.id)),
    ];
  };

  const toggleItem = (list, item) => {
    return list.some((it) => it.id === item.id)
      ? list.filter((it) => it.id !== item.id)
      : [...list, item];
  };

  const onSelected = useCallback(
    (item) => {
      setSelectedData((current) => toggleItem(current, item));
    },
    [setSelectedData],
  );

  const selectAll = (e) =>
    setSelectedData(
      e.target.checked ? (filterParams?.onlyItems ? vendorRFQ : tableData) : [],
    );

  // Fixed useEffect to avoid infinite loop by using raw data for source
  useEffect(() => {
    const source = filterParams?.onlyItems
      ? vendorRFQ || []
      : data?.vendorItems || [];

    const selectedFromServer = source.filter((item) => item.selected);

    if (!selectedFromServer.length) return;

    setSelectedData((prev) => mergeUniqueItems(prev, selectedFromServer));

    if (!filterParams?.onlyItems) {
      // const getSelected = vendorRFQ?.find((el) => el.selectedItem === true);
      const getSelectedIndex = vendorRFQ?.findIndex(
        (el) => el.selectedItem === true,
      );
      const getSelected = vendorRFQ?.[getSelectedIndex];
      setActiveTableIndex(getSelectedIndex);
      setSelectedID(getSelected?.id);
      setVendorAttachments([]);
      showRFQTable(getSelected?.vendor);
      setSelectedFilledForm(null);
    }
  }, [filterParams?.onlyItems, vendorRFQ]);

  const openOnlyPO = () => {
    const vendors = [...new Set(selectedData.map((item) => item.vendor))];

    setAllOpenPOs(
      vendors.map((vendor) => ({
        open: true,
        items: selectedData
          .filter((item) => item.vendor === vendor)
          .map((item) => {
            const copy = cloneDeep(item);
            delete copy.VendorsRFQs;
            return copy;
          }),
      })),
    );
  };

  const mergeSelectedById = ({ selectedData, tableData }) => {
    const tableMap = new Map(tableData.map((item) => [item.id, item]));

    return selectedData.map(
      (item) =>
        tableMap.has(item.id)
          ? { ...item, ...tableMap.get(item.id) } // update from tableData
          : item, // keep original
    );
  };

  const SaveSelectedItem = useMutation(
    (payload) =>
      fetchActionsUtil(
        `${backendUrl}/api/vendors/save-rfq-selected`,
        "post",
        "",
        payload,
      ),
    {
      onSuccess: ({ message }) => {
        queryClient.invalidateQueries("GET_COMPANY_RFQS");
        toast.success(message);
      },
      onError: ({ message = "" }) => {
        toast.error(`Unable to perform action: ${message}`);
      },
    },
  );

  const saveSelectedItems = async () => {
    const mainSelected = mergeSelectedById({ selectedData, tableData });
    // console.log({ mainSelected });
    const confirmed = await ConfirmDialog({
      title: "Save Items",
      description: "Are you sure you want to save these items",
    });

    if (confirmed) {
      SaveSelectedItem.mutate({ selectedData: mainSelected });
    }
  };

  const approveAndClose = async () => {
    const confirmed = await ConfirmDialog({
      title: "Approve And Close",
      description: "Are you sure you want to approve and close",
    });

    if (confirmed) {
      SaveSelectedItem.mutate({ selectedData, closed: true });
    }
  };

  const filteredItemDetails = useMemo(
    () => vendorRFQ?.filter((item) => item.amount > 0) || [],
    [vendorRFQ],
  );

  const alreadyHasFinalApproval = requisition?.requisitiontracks?.find(
    (track) => track.closedHere && track.receivedby === user?.Staff_ID,
  );

  const selectedDataLength = selectedData.length;

  return (
    <Modal
      show={show}
      onHide={() => onHide(false)}
      centered
      animation={false}
      enforceFocus={false}
      backdrop="static"
      fullscreen
    >
      <Modal.Header closeButton>
        <Modal.Title className="w-100">
          <div className="d-flex justify-content-between">
            <div>
              <h1 className="h5">{headerName}</h1>
            </div>
            <div>
              <h1 className="h6 mx-4">{headerNo}</h1>
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <main className="inner-page-outlet">
          <main className="cash-book">
            <div className="content">
              <HeaderArea
                filterParams={filterParams}
                setFilterParams={setFilterParams}
                refetch={refetch}
                data={data}
                VendorPayment={VendorPayment}
                sortOptions={sortOptions}
              />

              {filterParams?.onlyItems ? (
                <SelectedItemsTable
                  filteredItemDetails={filteredItemDetails}
                  selectedData={selectedData}
                  onSelected={onSelected}
                  selectAll={selectAll}
                  isSelected={selectedRFQ?.closed}
                />
              ) : (
                <div className="row">
                  <div className="col-4">
                    <VendorListTable
                      vendorRFQ={vendorRFQ}
                      isFetching={isFetching}
                      activeTableIndex={activeTableIndex}
                      selectedID={selectedID}
                      setSelectedID={setSelectedID}
                      setActiveTableIndex={setActiveTableIndex}
                      showRFQ={showRFQ}
                      setVendorAttachments={setVendorAttachments}
                      showRFQTable={showRFQTable}
                      setSelectedFilledForm={setSelectedFilledForm}
                    />

                    {!vendorRFQ.length && !isFetching && (
                      <div className="d-flex justify-content-center text-center w-100 my-4">
                        <NoTableItem queryParams={queryParams} />
                      </div>
                    )}
                  </div>

                  <div className="col-8">
                    {selectedID ? (
                      selectedFilledForm ? (
                        <PreviewFormOnlyComponent
                          selectedFilledForm={selectedFilledForm}
                          template={selectedFilledForm?.filledFormData}
                          setFetchingIframe={setFetchingIframe}
                          size="30rem"
                          canFill={false}
                          showTableCheckbox={false}
                          printForm
                        />
                      ) : (
                        <ItemTable
                          tableData={tableData}
                          selectedData={selectedData}
                          onSelected={onSelected}
                          selectAll={selectAll}
                          updateTableItem={updateTableItem}
                          disableInput={disableInput}
                          vendorAttachments={vendorAttachments}
                          backendUrl={backendUrl}
                          isSelected={selectedRFQ?.closed}
                        />
                      )
                    ) : (
                      <div className="d-flex justify-content-center text-center w-100 my-4">
                        <NoTableItem queryParams="No quote selected" />
                      </div>
                    )}

                    {!disableInput && selectedID && (
                      <div className="d-flex justify-content-end">
                        <button
                          onClick={sendDataUpdate}
                          className="btn btn-primary"
                          disabled={AddPriceMutation.isLoading}
                        >
                          {AddPriceMutation.isLoading
                            ? "Saving..."
                            : "Create Quote"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedDataLength > 0 && (
                <div className="d-flex justify-content-center my-3">
                  <button
                    className="btn btn-primary mx-4"
                    onClick={saveSelectedItems}
                    disabled={selectedRFQ?.closed}
                  >
                    Save Selected
                  </button>
                  {alreadyHasFinalApproval && (
                    <button
                      onClick={approveAndClose}
                      className="btn btn-primary"
                      disabled={selectedRFQ?.closed}
                    >
                      Approve and Close
                    </button>
                  )}

                  {selectedRFQ?.closed && (
                    <button
                      className="btn btn-primary mx-4"
                      onClick={openOnlyPO}
                      // disabled={selectedRFQ?.closed}
                    >
                      Create Purchase Order
                    </button>
                  )}
                </div>
              )}

              <div className="d-flex justify-content-between px-3 align-items-center pagination">
                <div className="pagination_left">
                  <p className="m-0 p-0">Show</p>
                  <select
                    value={queryParams.limit}
                    name="limit"
                    className="form-select"
                    onChange={handleSearchQueryChange}
                  >
                    <option value="10">10 rows</option>
                    <option value="20">20 rows</option>
                    <option value="30">30 rows</option>
                    <option value="40">40 rows</option>
                    <option value="50">50 rows</option>
                    <option value="100">100 rows</option>
                  </select>
                </div>

                <ReactPaginate
                  {...paginationOptions}
                  pageCount={Math.ceil((data?.count || 0) / queryParams.limit)}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={0}
                  onPageChange={({ selected }) => {
                    setQueryParams((prev) => ({
                      ...prev,
                      page: selected + 1,
                    }));
                    scrollToTop();
                  }}
                  forcePage={queryParams.page - 1}
                />
              </div>
            </div>
          </main>
        </main>

        {ShowNewRequisitionModal && (
          <PORequisitionModal
            setShowNewRequisitionModal={setShowNewRequisitionModal}
            ShowNewRequisitionModal={ShowNewRequisitionModal}
            selectedItems={selectedData}
          />
        )}

        {allOpenPos.map((item, index) => (
          <Fragment key={index}>
            <PORequisitionModal
              setShowNewRequisitionModal={(isOpen) =>
                setAllOpenPOs((old) =>
                  old.map((o, i) => ({
                    ...o,
                    open: i === index ? isOpen : o.open,
                  })),
                )
              }
              ShowNewRequisitionModal={item.open}
              selectedItems={item.items}
              disableVendor
              theRFQName={`${theRFQName} PO`}
            />
          </Fragment>
        ))}
      </Modal.Body>
    </Modal>
  );
}
